import {
    TableContainer,
    Paper,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Typography,
} from '@mui/material'
import { CompanyLicenseContract } from 'models'
import { useMemo } from 'react'
import { useFormat } from 'Services/localize/format'

interface IBasicRowInfo {
    caption: string
    value: string
    color?: string
}

interface SubscriptionTableProp {
    companyLicense: CompanyLicenseContract
    style?: React.CSSProperties
}

function exceedQuotaColor(
    quota: number | null | undefined,
    currentUsageCount: number | null | undefined,
) {
    if (!quota) return undefined

    return (currentUsageCount ?? 0) >= quota ? 'error' : undefined
}

function SubscriptionTable({ companyLicense, style }: SubscriptionTableProp) {
    const { humanDateFormat } = useFormat()
    const rows: IBasicRowInfo[] = useMemo(
        () => [
            {
                caption: 'Plan:',
                value: companyLicense?.licenseOfferType ?? 'N/A',
            },
            {
                caption: 'Location in use:',
                value:
                    companyLicense?.quota?.locationLimit === null
                        ? 'Unlimited'
                        : `${companyLicense?.locationUsed}/${companyLicense?.quota?.locationLimit}`,
                color: exceedQuotaColor(
                    companyLicense?.quota?.locationLimit,
                    companyLicense?.locationUsed,
                ),
            },
            {
                caption: 'SMS Count:',
                value:
                    companyLicense?.quota?.smsLimit === null
                        ? 'Unlimited'
                        : `${companyLicense?.smsUsed}/${companyLicense?.quota?.smsLimit}`,
                color: exceedQuotaColor(
                    companyLicense?.quota?.smsLimit,
                    companyLicense?.smsUsed,
                ),
            },
            {
                caption: 'Email Count:',
                value:
                    companyLicense?.quota?.emailLimit === null
                        ? 'Unlimited'
                        : `${companyLicense?.emailUsed}/${companyLicense?.quota?.emailLimit}`,
                color: exceedQuotaColor(
                    companyLicense?.quota?.emailLimit,
                    companyLicense?.emailUsed,
                ),
            },
            {
                caption: 'User Count:',
                value:
                    companyLicense?.quota?.userLimit === null
                        ? 'Unlimited'
                        : `${companyLicense?.userUsed}/${companyLicense?.quota?.userLimit}`,
                color: exceedQuotaColor(
                    companyLicense?.quota?.userLimit,
                    companyLicense?.userUsed,
                ),
            },
            {
                caption: 'Price estimate:',
                value: companyLicense?.expectedCostOfNextRenewal ?? 'N/A',
            },
            {
                caption: 'Billing frequency:',
                value: companyLicense?.billingFrequency ?? 'N/A',
            },
            {
                caption: 'Next bill date:',
                value:
                    humanDateFormat(companyLicense?.renewalDate ?? '') || 'N/A',
            },
        ],
        [companyLicense, humanDateFormat],
    )

    return (
        <TableContainer component={Paper} style={style}>
            <Table size="small">
                <TableBody>
                    {rows.map((row) => (
                        <TableRow
                            key={row.caption}
                            sx={{
                                '&:last-child td, &:last-child th': {
                                    border: 0,
                                },
                            }}
                        >
                            <TableCell
                                component="th"
                                scope="row"
                                style={{ fontWeight: 'bold' }}
                            >
                                {row.caption}
                            </TableCell>
                            <TableCell align="right">
                                <Typography color={row.color}>
                                    {row.value}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default SubscriptionTable
